/* eslint-disable max-len */
import { inject, Injectable } from '@angular/core';
import {
  Observable,
  Subject,
  catchError,
  firstValueFrom,
  interval,
  map,
  merge,
  of,
  scan,
  takeUntil,
  tap,
} from 'rxjs';
import { MuHttpService } from './mu-http.service';
import { environment } from 'src/environments/environment';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { RespGetPointsByCompany } from '../../operations/models/interface/gata-get-companies-by-id.interface';
import { Point } from '../../points/models/interface/point.interface';
import { IndexedDbService } from './indexed-db.service';
import { UserDataShared } from '../models/interface/userData.interface';
import { CookieService } from 'ngx-cookie-service';
import { MuCryptService } from './mu-crypt.service';
import { CompanyUserService } from './company-user.service';

@Injectable({
  providedIn: 'root',
})
export class PointsUserService {
  private pointsUpdated$ = new Subject<{
    progress: number;
    allPoints: Point[];
    currentPoints: Point[];
  }>();
  cancelLoadOrders$ = new Subject<void>();
  cancelLoad$ = new Subject<void>();
  allPointsDb: Point[] = [];
  storyName = 'points_store';
  cookieService = inject(CookieService);

  constructor(
    private muHttp: MuHttpService,
    private indexedDbService: IndexedDbService,
    private muCryptService: MuCryptService<any>,
    private companyUserService: CompanyUserService
  ) {}

  getPointsUpdates(): Observable<{ progress: number; allPoints: Point[]; currentPoints: Point[] }> {
    return this.pointsUpdated$.asObservable();
  }

  async loadPointsUser(userData: UserDataShared) {
    let companies: number[] = [];
    try {
      companies = (
        (await this.companyUserService.getAssociatedCompanies(userData.companyId)) || []
      ).map(company => company.companyId);
      //Se pone de primero el id que tiene el usuario en sus datos para que se liste
      //primero los puntos iniciales.
      const index = companies.indexOf(parseInt(userData.companyId));
      if (index !== -1) {
        const [foundValue] = companies.splice(index, 1);
        companies.unshift(foundValue);
      }
    } catch (error) {
      companies = [parseInt(userData.companyId)];
    }
    // Llamar la primera vez
    this.loadPoints(companies, userData.id);
    //  Intervalo para llamar `loadPoints` cada 20 minutos (1200000ms)
    interval(environment.timeReloadPointsUser)
      .pipe(takeUntil(this.cancelLoad$))
      .subscribe(() => {
        this.loadPoints(companies, userData.id);
      });
  }

  async loadPoints(companies: number[] = [], userId: string = '') {
    this.cancelLoadOrders();
    this.cancelLoadOrders$ = new Subject<void>();
    let progressAll = 0;
    this.allPointsDb = await this.getAllPoints();
    this.getPointsList(companies, userId)
      .pipe(
        tap(async ({ progress, currentPoints, points }) => {
          progressAll = progress;
          // filtro por valores validos
          const newCurrentPoints = currentPoints.filter(
            point => point.active && point?.pointId && point?.cityId
          );
          const newAlltPoints = points.filter(
            point => point.active && point?.pointId && point?.cityId
          );
          //Almaceno en las cookies
          this.cookieService.set('progress_points', progressAll.toString(), {
            expires: environment.timeVerifyVersion,
          });
          //Emito los valores
          this.pointsUpdated$.next({
            progress: progressAll,
            currentPoints: newCurrentPoints,
            allPoints: newAlltPoints,
          });
          this.processWithTimeout(newCurrentPoints, 50);
        }),
        takeUntil(this.cancelLoadOrders$)
      )
      .subscribe({
        complete: () => {
          this.cookieService.set('progress_points', progressAll.toString(), {
            expires: environment.timeVerifyVersion,
          });
          this.cookieService.set('progress_points_complete', 'true', {
            expires: environment.timeVerifyVersion,
          });
        },
      });
  }

  async processWithTimeout(points: Point[], batchSize: number) {
    // Dividimos los puntos en lotes
    const batches = Array.from({ length: Math.ceil(points.length / batchSize) }, (_, i) =>
      points.slice(i * batchSize, (i + 1) * batchSize)
    );

    // Procesamos los lotes de forma secuencial
    await batches.reduce(async (previousBatchPromise, batch) => {
      // Espera a que el lote anterior termine
      await previousBatchPromise;

      // Procesar el lote actual
      const promises = batch.map(async point => {
        try {
          const existingPoint = this.allPointsDb.find(item => item.pointId === point.pointId);

          if (!existingPoint) {
            // Encriptar y agregar el punto
            const encryptedPoint = this.muCryptService.encrypt(point);
            await firstValueFrom(
              this.indexedDbService.addData(this.storyName, {
                data: encryptedPoint,
                pointId: point.pointId.toString(),
              })
            );
          } else {
            const isDifferent = JSON.stringify(existingPoint) !== JSON.stringify(point);
            if (!isDifferent) return;

            const encryptedPoint = this.muCryptService.encrypt(point);
            await firstValueFrom(
              this.indexedDbService.updateData(this.storyName, point.pointId.toString(), {
                data: encryptedPoint,
                pointId: point.pointId.toString(),
              })
            );
          }
        } catch (error) {
          console.error(`Error procesando el punto ${point.pointId}:`, error);
        }
      });

      // Esperar a que se completen todas las promesas del lote
      await Promise.all(promises);

      // Pausa para liberar el navegador
      await new Promise(resolve => setTimeout(resolve, 10)); // Ajusta el tiempo de pausa según sea necesario
    }, Promise.resolve()); // Inicializamos con una promesa resuelta
  }

  async getAllPoints(): Promise<Point[]> {
    const encryptedPoints = await firstValueFrom(this.indexedDbService.getAllData(this.storyName));
    const points = encryptedPoints.map(point => this.muCryptService.decrypt(point.data));
    return points;
  }

  getPointsList(
    companyIds: number[] = [],
    userId: string = ''
  ): Observable<{ points: Point[]; progress: number; currentPoints: Point[] }> {
    const totalRequests = companyIds.length;
    let completedRequests = 0;

    if (totalRequests === 0) {
      // Si no hay compañías, devolver un observable vacío
      return of({ points: [], progress: 100, currentPoints: [] });
    }

    const requests = companyIds.map(companyId => {
      return this.muHttp
        .post(`${environment.hostInternal}/company/points/services/get-points-list`, {
          companyListId: [companyId],
          userId: parseInt(userId),
        })
        .pipe(
          catchError((error: HttpErrorResponse) => {
            const errorMessage = `Request failed for company ${companyId}: ${error.message}`;
            console.error(errorMessage);
            return of({
              points: [], // Devolver puntos vacíos en caso de error
              status: {
                errorCode: error.status,
                message: 'Error',
                status: 'Error',
              },
            });
          }),
          map((response: RespGetPointsByCompany) => {
            // Asegúrate de que `response.points` es un array
            if (response && Array.isArray(response.points)) {
              return response.points.map(point => ({ ...point, companyId }));
            }
            return []; // Retorna un array vacío si no hay puntos
          })
        );
    });

    return merge(...requests).pipe(
      tap(() => {
        completedRequests++;
      }),
      scan(
        (acc, points: Point[]) => {
          const progress = Math.ceil((completedRequests / totalRequests) * 100);
          return {
            points: acc.points.concat(points ? points : []),
            progress,
            currentPoints: points ? points : [],
          };
        },
        { points: [], progress: 0, currentPoints: [] }
      )
    );
  }

  cancelLoadOrders() {
    if (!this.cancelLoadOrders$.closed) {
      this.cancelLoadOrders$.next(); // Emitir la señal de cancelación
      this.cancelLoadOrders$.complete(); // Completar el subject
      this.cancelLoadOrders$.unsubscribe();
    }
  }
}
